/**
 * EVBreakdownMultipleChart — peer comparison of the EV/EBITDA multiple, stacked
 * by where the value comes from:
 *   - Current EBIT (current operations)
 *   - Future Growth
 *   - Future Market Expectation
 *
 * One bar per company for a single, user-selectable fiscal year (defaults to the
 * latest year the focal company has decomposition data for). The focal company
 * is starred; a growth-contribution insight sits below the chart.
 *
 * Unlike EVBreakdownPercentageChart (which normalises each bar to 100%), this
 * shows the ABSOLUTE multiples stacked, so bar height is the total EV/EBITDA —
 * mirroring the source Financial-Insight-Dashboard "Enterprise Value Breakdown"
 * chart. Peer-mode only: rendered when competitor data is present.
 */
import { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import ChartCard, { FocalBadge, focalTickFactory } from './ChartCard';
import ChartTooltip from './ChartTooltip';
import {
  EV_SEGMENT,
  BAR_MAX_SIZE,
  gridProps,
  yAxisProps,
  legendProps,
  zeroLineProps,
  CHART_TYPE,
} from './chartTheme';
import { formatFiscalYear } from '../utils/formatters';
import type { UnifiedFinancialRecord, RegionConfig } from '../../../types/financial';

interface Props {
  /** Focal company records (full history, so the year picker offers every year). */
  data: UnifiedFinancialRecord[];
  region?: RegionConfig;
  companyName: string;
  competitorData: Record<string, UnifiedFinancialRecord[]>;
}

const EBIT_KEY = 'EV/EBITDA from Current EBIT';
const GROWTH_KEY = 'EV/EBITDA from Future Growth';
const MARKET_KEY = 'EV/EBITDA from Future Market Expectation';

function hasDecomposition(r: UnifiedFinancialRecord): boolean {
  return (
    r.ev_from_ebit_ebitda_multiple != null ||
    r.ev_from_future_growth_ebitda_multiple != null ||
    r.ev_from_future_market_expectation != null
  );
}

function componentsForYear(records: UnifiedFinancialRecord[], year: number) {
  const r = records.find((x) => x.year === year);
  if (!r || !hasDecomposition(r)) return null;
  return {
    ebit: r.ev_from_ebit_ebitda_multiple ?? 0,
    growth: r.ev_from_future_growth_ebitda_multiple ?? 0,
    market: r.ev_from_future_market_expectation ?? 0,
    record: r,
  };
}

/** Latest year (descending) for which the given records carry decomposition data. */
function latestDecompYear(records: UnifiedFinancialRecord[]): number | null {
  const years = records.filter(hasDecomposition).map((r) => r.year);
  return years.length ? Math.max(...years) : null;
}

export default function EVBreakdownMultipleChart({
  data,
  region,
  companyName,
  competitorData,
}: Props) {
  // The union of years ANY company has decomposition data for, newest first,
  // drives the picker. Defaults to the focal's latest year (Task: "show latest
  // year data") so the chart opens on the most recent comparable period.
  const yearSet = new Set<number>();
  for (const recs of [data, ...Object.values(competitorData)]) {
    for (const r of recs) if (hasDecomposition(r)) yearSet.add(r.year);
  }
  const years = Array.from(yearSet).sort((a, b) => b - a);

  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const defaultYear = latestDecompYear(data) ?? years[0] ?? null;
  const activeYear = selectedYear ?? defaultYear;

  if (activeYear == null || years.length === 0) return null;

  const focal = componentsForYear(data, activeYear);

  const rawRows = [
    focal ? { name: companyName, isFocal: true, ...focal } : null,
    ...Object.entries(competitorData).map(([name, recs]) => {
      const c = componentsForYear(recs, activeYear);
      return c ? { name, isFocal: false, ...c } : null;
    }),
  ].filter(
    (r): r is {
      name: string;
      isFocal: boolean;
      ebit: number;
      growth: number;
      market: number;
      record: UnifiedFinancialRecord;
    } => r != null
  );

  // Feature-not-available (no comparable data for the year) — not a no-data card.
  if (rawRows.length === 0) return null;

  const hasMarket = rawRows.some((r) => Math.abs(r.market) > 0);

  const chartData = rawRows
    .map((r) => ({
      name: r.name,
      isFocal: r.isFocal,
      [EBIT_KEY]: parseFloat(r.ebit.toFixed(2)),
      [GROWTH_KEY]: parseFloat(r.growth.toFixed(2)),
      ...(hasMarket ? { [MARKET_KEY]: parseFloat(r.market.toFixed(2)) } : {}),
      _total: r.ebit + r.growth + (hasMarket ? r.market : 0),
    }))
    .sort((a, b) => b._total - a._total);

  // Growth contribution for the focal company: prefer the backend's own
  // percentage, else derive it from EV_from_Future_Growth / EV.
  const fr = focal?.record;
  const growthContribution =
    fr?.future_growth_percentage != null
      ? fr.future_growth_percentage
      : fr && fr.ev_from_future_growth != null && fr.enterprise_value
        ? (fr.ev_from_future_growth / fr.enterprise_value) * 100
        : null;

  const yearPicker = (
    <select
      value={activeYear}
      onChange={(e) => setSelectedYear(Number(e.target.value))}
      className="text-xs border border-neutral-200 rounded-md px-2.5 py-1.5 bg-white text-neutral-700 focus:outline-none focus:ring-1 focus:ring-primary-400"
      aria-label="Select fiscal year"
    >
      {years.map((y) => (
        <option key={y} value={y}>
          {formatFiscalYear(y, region)}
        </option>
      ))}
    </select>
  );

  const insight =
    growthContribution != null ? (
      <div className="text-xs text-neutral-600 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span>
          <span className="font-semibold text-neutral-800">Growth Contribution for {companyName}:</span>{' '}
          {growthContribution.toFixed(1)}% of total EV
        </span>
        <span className={growthContribution > 50 ? 'text-emerald-600' : 'text-primary-600'}>
          {growthContribution > 50
            ? '✓ High growth expectations priced in'
            : 'ℹ Value primarily from current operations'}
        </span>
      </div>
    ) : undefined;

  return (
    <ChartCard
      title="Enterprise Value Breakdown: Current vs Future Growth"
      subtitle={`EV/EBITDA multiple for ${formatFiscalYear(activeYear, region)} split across current operations, future growth${hasMarket ? ' and market expectation' : ''} — one bar per company`}
      action={
        <div className="flex items-center gap-2">
          {yearPicker}
          <FocalBadge name={companyName} />
        </div>
      }
      footnote={insight}
    >
      <ResponsiveContainer width="100%" height={360}>
        <BarChart data={chartData} margin={{ top: 16, right: 16, left: 8, bottom: 8 }}>
          <CartesianGrid {...gridProps} />
          <XAxis dataKey="name" interval={0} height={110} tick={focalTickFactory(companyName)} />
          <YAxis
            {...yAxisProps}
            width={48}
            domain={['auto', 'auto']}
            tickFormatter={(v) => `${v?.toFixed(0)}x`}
          />
          <ReferenceLine y={0} {...zeroLineProps} />
          <Tooltip content={<ChartTooltip format={(v) => `${v.toFixed(2)}x`} />} />
          <Legend
            {...legendProps}
            verticalAlign="top"
            align="right"
            wrapperStyle={{ fontSize: CHART_TYPE.legend, paddingBottom: 8 }}
          />
          <Bar dataKey={EBIT_KEY} stackId="ev" fill={EV_SEGMENT.ebit} maxBarSize={BAR_MAX_SIZE} />
          <Bar dataKey={GROWTH_KEY} stackId="ev" fill={EV_SEGMENT.growth} maxBarSize={BAR_MAX_SIZE} radius={hasMarket ? undefined : [3, 3, 0, 0]} />
          {hasMarket && <Bar dataKey={MARKET_KEY} stackId="ev" fill={EV_SEGMENT.market} maxBarSize={BAR_MAX_SIZE} radius={[3, 3, 0, 0]} />}
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
